/**
 * DashboardFavoriteTools - tools sorted by derived usage this week.
 *
 * Tool set + icons + paths + fallback come from useNavigationPreferences;
 * last-used + weekly count are merged from the derived tool-usage read
 * path (no new tracking). Tools without usage keep favorite order after
 * used ones; empty/failed usage → plain favorites (no crash).
 */
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { useNavigationPreferences } from '@/hooks/useNavigationPreferences'
import { getDashboardToolUsage } from '@/lib/api/dashboard-tools'
import { EID } from '@/lib/element-ids.registry'
import { DashboardToolCard } from './DashboardToolCard'

export function DashboardFavoriteTools() {
  const { t } = useTranslation()
  const { organizedTools, isLoading } = useNavigationPreferences()
  const favorites = organizedTools.favorites

  const { data: usage } = useQuery({
    queryKey: ['dashboard-tool-usage'],
    queryFn: getDashboardToolUsage,
    staleTime: 60_000,
  })

  const ranked = useMemo(() => {
    const byId = new Map((usage ?? []).map((u) => [u.tool_id, u]))
    return favorites
      .map((tool, index) => {
        const u = byId.get(tool.id)
        return {
          tool,
          index,
          count: u?.count_this_week ?? 0,
          lastUsedAt: u?.last_used_at ?? null,
        }
      })
      .sort((a, b) => b.count - a.count || a.index - b.index)
  }, [favorites, usage])

  if (isLoading) return null

  return (
    <section id={EID['dashboard-favorites-section']} className="dashboard-favorite-tools">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-baseline gap-2">
          <h2 className="text-base font-bold text-neutral-900 dark:text-white">
            {t('dashboardTools.title')}
          </h2>
          <span className="text-sm text-neutral-500">{t('dashboardTools.heading')}</span>
        </div>
        <Link
          id={EID['dashboard-favorites-all-tools-link']}
          to="/v2/tools"
          className="flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
        >
          {t('dashboardTools.allTools')}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      {ranked.length > 0 ? (
        <div
          data-testid={EID['dashboard-favorites-list']}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3"
        >
          {ranked.map(({ tool, count, lastUsedAt }) => (
            <DashboardToolCard
              key={tool.id}
              name={tool.name}
              path={tool.path}
              Icon={tool.icon}
              lastUsedAt={lastUsedAt}
              count={count}
              description={t(`dashboardTools.desc.${tool.id}`, '')}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-400 dark:text-neutral-600">
          {t('dashboardTools.empty')}{' '}
          <Link
            to="/v2/tools"
            className="underline hover:text-neutral-600 dark:hover:text-neutral-400"
          >
            {t('dashboardTools.allTools')}
          </Link>
        </p>
      )}
    </section>
  )
}
